import React, {
  useState,
  useRef,
  Suspense,
  lazy,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { useSearchParams } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { AxiosResponse } from "axios";
import { CreateNewBooking } from "../../@types/DTO/Booking";
import { BookingApi } from "../../API/Apis/BookingApi";
import { CustomerDetailsResponse_2 } from "../../@types/DTO/ApiResponses";
import {
  BookingDetailsDefaultValues,
  DefaultValuesDetailsForm,
  DetailsFormValues,
} from "../../components/Layouts/WebCheckInForms/WebCheckInForms.props";
import { PrivacyPolicy } from "../../@types/DTO/privacyPolicy";
import "../../App.css";
import { useTranslation } from "react-i18next";
import UploadVoucher from "./UploadVoucher";
import WebCheckInHome from "./WebCheckInHome";
import { calculateNestedProgress } from "../../helpers/calculateNestedProgress";
import { isDogmaAiEnabled } from "../../helpers/dogmaAi";
import BookingGetters from "../../store/onlineBooking/getters";
import NotAuthorisedPage from "../notAuthorisedPage";
import i18n from "../../config/i18n";
const preloadComponents = () => {
  const components = [
    import("./SearchByVoucherNumber"),
    import("./SearchByReservationNumber"),
    import("./WebCheckInPopUp"),
    import("./ReservationDetails"),
    import("./PrivacyPolicyForm"),
    import("./RentalContractFirstDriver"),
    import("./UploadDocuments"),
    import("./DriverDetails"),
    import("./WebCheckCompletionPage"),
    import("./AdditionalDriver"),
    import("./BillingDetails"),
    import("./CardWithLoader"),
  ];
  components.forEach((component) => component.catch(() => {}));
};

const SearchByVoucherNumber = lazy(() => import("./SearchByVoucherNumber"));
const SearchByReservationNumber = lazy(
  () => import("./SearchByReservationNumber")
);
const WebCheckInPopUp = lazy(() => import("./WebCheckInPopUp"));
const ReservationDetails = lazy(() => import("./ReservationDetails"));
const PrivacyPolicyForm = lazy(() => import("./PrivacyPolicyForm"));
const RentalContractFirstDriver = lazy(
  () => import("./RentalContractFirstDriver")
);
const UploadDocuments = lazy(() => import("./UploadDocuments"));
const DriverDetails = lazy(() => import("./DriverDetails"));
const WebCheckCompletionPage = lazy(() => import("./WebCheckCompletionPage"));
const AdditionalDriver = lazy(() => import("./AdditionalDriver"));
const BillingDetailsForm = lazy(() => import("./BillingDetails"));
const CardWithLoader = lazy(() => import("./CardWithLoader"));

export default function NewWebCheckInPage() {
  const [navStack, setNavStack] = useState<string[]>(["UploadVoucher"]);
  const [queryParams] = useSearchParams();

  useEffect(() => {
    const langParam = queryParams.get("lang");
    if (
      langParam &&
      i18n.options.supportedLngs &&
      (i18n.options.supportedLngs as string[]).includes(langParam)
    ) {
      i18n.changeLanguage(langParam);
    }
  }, [queryParams]);

  const goToPage = (nextPage: string) => {
    setNavStack((prev) => [...prev, nextPage]);
  };

  const goBack = () => {
    setNavStack((prev) => prev.slice(0, -1));
  };

  const page = navStack[navStack.length - 1];

  const [currentDriver, setCurrentDriver] = useState(0);
  const { t: translate } = useTranslation();
  const [openModal, setOpenModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");

  const handleClose = useCallback(() => {
    setOpenModal(false);
  }, []);

  const [showDocumentInput, setShowDocumentInput] = useState("");

  const [initialCustomerDetails, setInitialCustomerDetails] =
    useState<DetailsFormValues>({
      ...DefaultValuesDetailsForm,
    });
  const [currentCustomerDetails, setCurrentCustomerDetails] =
    useState<DetailsFormValues>({
      ...initialCustomerDetails,
    });
  const [reservationDetails, setReservationDetails] =
    useState<CreateNewBooking>({
      ...BookingDetailsDefaultValues,
    });
  const [allPrivacyPolicy, setAllPrivacyPolicy] = useState<
    PrivacyPolicy["privacyPolicy"]
  >([]);
  const [selectedPrivacyPolicy, setSelectedPrivacyPolicy] = useState<string[]>(
    []
  );

  useEffect(() => {
    preloadComponents();
  }, []);

  function getPrivacyPolicy(reservationId: number) {
    BookingApi.getPrivacyPolicy(reservationId, "booking").then((response) => {
      const fetchedPolicies = response.data.privacyPolicy.map((item) => {
        const policyGroup = item.policyGroup;
        return policyGroup;
      });
      setAllPrivacyPolicy(fetchedPolicies);
      const selectedPrivacyPolicy = response.data.selectedPolicies || [];
      setSelectedPrivacyPolicy(selectedPrivacyPolicy);
    });
  }

  const [documentTypes, setDocumentTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const appSettings = BookingGetters.getAppSettings();
  const preventMultipleWebCheckin = appSettings?.preventMultipleWebCheckin;
  const [lastSearchValues, setLastSearchValues] =
    useState<DetailsFormValues | null>(null);

  const [resDateIsPassed, setResDateIsPassed] = useState<string>("");
  const [rezIsRental, setRezIsRental] = useState<string>("");
  const [isWebCheckInDone, setIsWebCheckInDone] = useState<string>("");
  const [lowQualityUploadedImage, setLowQualityUploadedImage] =
    useState<string>("");
  const [initialPrimaryDriverUpload, setInitialPrimaryDriverUpload] =
    useState<boolean>(true);
  const locationLang = i18n.language;
  const textResourceDetails =
    BookingGetters.getTextResourceDetailsBasedOnLang();
  const textResourceData =
    textResourceDetails?.[locationLang]?.flatMap((item) => item.data || []) ||
    [];
  useEffect(() => {
    const resDateIsPassedData = textResourceData.find(
      (item) => item.key === "resDateIsPassed"
    );
    setResDateIsPassed(
      resDateIsPassedData?.body || translate("res_Date_Is_Passed")
    );
    const rezIsRentalData = textResourceData.find(
      (item) => item.key === "rezIsRental"
    );
    setRezIsRental(rezIsRentalData?.body || translate("rez_Is_Rental"));
    const isWebCheckInDoneData = textResourceData.find(
      (item) => item.key === "isWebCheckInDone"
    );
    setIsWebCheckInDone(
      isWebCheckInDoneData?.body || translate("is_Web_Check_In_Done")
    );
    const lowQualityUploadedImageData = textResourceData.find(
      (item) => item.key === "lowQualityUploadedImage"
    );
    setLowQualityUploadedImage(
      lowQualityUploadedImageData?.body ||
        translate("low_Quality_Uploaded_Image")
    );
  }, [textResourceData, locationLang, translate]);

  async function handleReservationResponse(
    reservationApiResponse: AxiosResponse<CreateNewBooking>,
    data: DetailsFormValues
  ): Promise<boolean> {
    const isInvalidReservation =
      reservationApiResponse?.data?.rezIsCancelled === "true";
    if (isInvalidReservation) {
      setModalMessage(
        translate(
          "Web check-in is unavailable because this reservation has been cancelled."
        )
      );
      setOpenModal(true);
      return false;
    }

    const pickUpDate = reservationApiResponse.data.pickUpDate;
    const pickUpTime = reservationApiResponse.data.pickUpTime;
    const reservationDateTime = new Date(`${pickUpDate}T${pickUpTime}`);
    const newDate = new Date();

    // default buffer = 1 hour
    const bufferHours = appSettings?.additionBufferHourForWebCheckin
      ? Number(appSettings.additionBufferHourForWebCheckin)
      : 1;
    const reservationDateTimeWithBuffer = new Date(
      reservationDateTime.getTime() + bufferHours * 60 * 60 * 1000
    );
    if (
      reservationApiResponse?.data?.status === "error" &&
      reservationApiResponse?.data?.msg ===
        "The reservation has already been converted into a rental"
    ) {
      setModalMessage(rezIsRental);
      setOpenModal(true);
      return false;
    } else if (reservationApiResponse?.data?.status === "error") {
      setModalMessage(
        translate("Booking not found.\nPlease verify your details.")
      );
      setOpenModal(true);
      return false;
    } else if (reservationDateTimeWithBuffer < newDate) {
      setModalMessage(resDateIsPassed);
      setOpenModal(true);
      return false;
    } else if (
      reservationApiResponse.data.isWebCheckInDone === "true" &&
      preventMultipleWebCheckin
    ) {
      setModalMessage(isWebCheckInDone);
      setOpenModal(true);
      return false;
    }

    getPrivacyPolicy(reservationApiResponse.data.reservationId);
    setDocumentTypes(reservationApiResponse.data.documentTypes);

    const customerDetails: AxiosResponse<CustomerDetailsResponse_2> =
      await BookingApi.getCustomerDetails(
        reservationApiResponse.data.customerId
      );

    const tempValues: DetailsFormValues = {
      ...data,
      ...customerDetails.data,
      isDriver: customerDetails.data.isDriver ? "YES" : "NO",
      customerType: customerDetails.data.customerType.toUpperCase(),
    };

    setInitialCustomerDetails({ ...tempValues });
    setReservationDetails({ ...reservationApiResponse.data });
    await setCurrentCustomerDetails({ ...tempValues });

    goToPage("ReservationDetailsForm");
    return true;
  }

  async function getBookingDetailsByReservationNumber(
    data: DetailsFormValues
  ): Promise<void> {
    try {
      setIsLoading(true);
      if (!data.reservationDate) {
        return;
      }

      const reservationApiResponse: AxiosResponse<CreateNewBooking> =
        await BookingApi.getBookingDetailsByBookingDateAndReservationCode(
          data.reservationPrefix || "",
          data.reservationNumber || "",
          new Date(data.reservationDate)
        );

      await handleReservationResponse(reservationApiResponse, data);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }

  async function getBookingDetailsByVoucherCodeAndFullName(
    data: DetailsFormValues
  ): Promise<void> {
    try {
      setIsLoading(true);
      const reservationApiResponse =
        await BookingApi.getBookingDetailsByVoucherCode(
          data.reservationVoucher || "",
          data.firstName || "",
          data.lastName || "",
          data.companyName || ""
        );

      await handleReservationResponse(reservationApiResponse, data);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }

  const [initialDriverValues, setInitialDriverValues] = useState<
    DetailsFormValues[]
  >([]);
  const [allDriverDetails, setAllDriverDetails] = useState<DetailsFormValues[]>(
    []
  );
  const allDriverCount = allDriverDetails.length;

  function setDriverDetailsAndInitialDriverIndexAtIndex(
    driverDetails: any,
    index: number
  ) {
    const mapped: any = {
      ...DefaultValuesDetailsForm,
      ...driverDetails,
      isDriver: !driverDetails.isDriver ? "YES" : "NO",
      reservationNumber: JSON.stringify(reservationDetails.reservationId),
      releaseDate: driverDetails.releaseDate || "",
      issueBy: driverDetails.issueBy || driverDetails.issuedBy || "",
    };

    setAllDriverDetails((prev: DetailsFormValues[]) => {
      prev[index] = {
        ...mapped,
        drivingLicenseDocuments: prev[index]?.drivingLicenseDocuments,
        identificationDocuments: prev[index]?.identificationDocuments,
      };
      return [...prev];
    });

    setInitialDriverValues((prev) => {
      prev[index] = mapped;
      return [...prev];
    });
  }
  useEffect(() => {
    if (reservationDetails?.driver1Id) {
      BookingApi.getCustomerDetails(reservationDetails.driver1Id).then((e) => {
        setDriverDetailsAndInitialDriverIndexAtIndex(
          {
            ...e.data,
          },
          0
        );
        // If this driver was previously registered via addDrivers, skip the
        // 3-section upload cycle so DriverDetails shows their saved data.
        if (e.data.isDriver === true) {
          setInitialPrimaryDriverUpload(false);
        }
      });
    }
    if (reservationDetails?.driver2Id) {
      BookingApi.getCustomerDetails(reservationDetails.driver2Id).then((e) => {
        setDriverDetailsAndInitialDriverIndexAtIndex(e.data, 1);
      });
    }
    if (reservationDetails?.driver3Id) {
      BookingApi.getCustomerDetails(reservationDetails.driver3Id).then((e) => {
        setDriverDetailsAndInitialDriverIndexAtIndex(e.data, 2);
      });
    }
  }, [reservationDetails]);
  const [enableDogmaAI, setEnableDogmaAI] = useState<boolean | null>(null);
  const [isSettingsLoading, setIsSettingsLoading] = useState(true);
  const [showCompatibilityError, setShowCompatibilityError] = useState(false);
  const country = appSettings?.config?.country;

  useEffect(() => {
    const fetchSettings = async () => {
      let aiUploadEnabled = false;
      try {
        const appSettings = await BookingApi.getAppSetting();
        aiUploadEnabled = isDogmaAiEnabled(appSettings);
        setEnableDogmaAI(aiUploadEnabled);
      } catch (error: any) {
        setEnableDogmaAI(isDogmaAiEnabled(null));
        const isMobile = /iPhone|Android/i.test(navigator.userAgent);
        const isNetworkError =
          !error?.response ||
          error?.code === "ERR_NETWORK" ||
          error?.message === "Network Error";
        if (isMobile && isNetworkError) {
          setShowCompatibilityError(true);
        }
      } finally {
        if (!aiUploadEnabled) {
          setNavStack(["WebCheckInHome"]);
        }
        setIsSettingsLoading(false);
      }
    };

    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When AI is off, UploadVoucher must never be visible regardless of how the
  // nav stack was built (e.g. Back from BillingDetails in the prefill path).
  // enableDogmaAI===null means the fetch is still in-flight; only act on false.
  useEffect(() => {
    if (page === "UploadVoucher" && enableDogmaAI === false) {
      setNavStack(["WebCheckInHome"]);
    }
  }, [page, enableDogmaAI]);

  const handleDriverDetailsNext = (updatedDrivers: any[]) => {
    setIsLoading(false);
    setAllDriverDetails(updatedDrivers);
    setReservationDetails((prev) => ({
      ...prev,
      driver1Id: updatedDrivers[0]?.customerId || prev.driver1Id,
      driver2Id: updatedDrivers[1]?.customerId || prev.driver2Id,
      driver3Id: updatedDrivers[2]?.customerId || prev.driver3Id,
    }));
    goToPage("AdditionalDriver");
  };

  const isSmallScreen = useMemo(() => window.innerWidth <= 500, []);

  const SuspenseWrapper = ({ children }: { children: React.ReactNode }) => (
    <Suspense
      fallback={
        <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
          <CircularProgress sx={{ color: "#19344F" }} />
        </Box>
      }
    >
      {children}
    </Suspense>
  );

  //  reset all the state variables to their initial values
  const resetToInitialState = useCallback(() => {
    setIsLoading(false);
    goToPage(enableDogmaAI ? "UploadVoucher" : "WebCheckInHome");
    setCurrentDriver(0);
    setOpenModal(false);
    setModalMessage("");
    setShowDocumentInput("");
    setInitialCustomerDetails({ ...DefaultValuesDetailsForm });
    setCurrentCustomerDetails({ ...DefaultValuesDetailsForm });
    setReservationDetails({ ...BookingDetailsDefaultValues });
    setAllPrivacyPolicy([]);
    setSelectedPrivacyPolicy([]);
    setDocumentTypes([]);
    setInitialDriverValues([]);
    setAllDriverDetails([]);
    setLastSearchValues([]);
    setDocStep(1);
    setInitialPrimaryDriverUpload(true);
    primaryDriverExtractedDataRef.current = null;
    primaryDriverFilesRef.current = { idDocs: [], licenseDocs: [] };
  }, [enableDogmaAI]);

  const [bgLoaded, setBgLoaded] = useState(false);

  useEffect(() => {
    const img = new Image();
    img.src = "/assets/images/homePageImage.jpg";
    img.onload = () => setBgLoaded(true);
  }, []);

  const [docStep, setDocStep] = useState(1);
  const primaryDriverExtractedDataRef = useRef<any>(null);
  const primaryDriverFilesRef = useRef<{
    idDocs: File[];
    licenseDocs: File[];
  }>({ idDocs: [], licenseDocs: [] });

  const handleNextFromDocs = async (data?: any) => {
    if (showDocumentInput === "yes" && initialPrimaryDriverUpload) {
      // Accumulate OCR data across all 3 upload steps
      // if (data?.extractedData) {
      //   primaryDriverExtractedDataRef.current = {
      //     ...(primaryDriverExtractedDataRef.current || {}),
      //     ...data.extractedData,
      //   };
      // }

      // Capture uploaded files from section 1
      // if (data?.files) {
      //   const { driverLicenseFront, driverLicenseBack, idFront, idBack } =
      //     data.files;
      //   primaryDriverFilesRef.current = {
      //     idDocs: [idFront, idBack].filter(Boolean) as File[],
      //     licenseDocs: [driverLicenseFront, driverLicenseBack].filter(
      //       Boolean
      //     ) as File[],
      //   };
      // }

      if (docStep < 3) {
        setDocStep(docStep + 1);
      } else {
        // const capturedDocData = primaryDriverExtractedDataRef.current || {};
        // const capturedFiles = primaryDriverFilesRef.current;
        // primaryDriverExtractedDataRef.current = null;
        // primaryDriverFilesRef.current = { idDocs: [], licenseDocs: [] };

        // Link the customer as driver 1 on the reservation
        const setDriverResponse =
          await BookingApi.reservationSetCustomerAsDriver1(
            reservationDetails.reservationId
          );

        // Refresh allDriverDetails[0] using driver1Id from the API response
        const driver1Id =
          (setDriverResponse as any)?.data?.driver1Id ||
          (setDriverResponse as any)?.driver1Id;
        if (driver1Id) {
          const refreshed = await BookingApi.getCustomerDetails(driver1Id);
          setDriverDetailsAndInitialDriverIndexAtIndex(
            { ...refreshed.data },
            0
          );
        }
        setInitialPrimaryDriverUpload(false);
        // setAllDriverDetails((prev) => {
        //   const updated = [...prev];
        //   updated[0] = {
        //     ...updated[0],
        //     ...(currentCustomerDetails || {}),
        //     ...capturedDocData,
        //     customerId:
        //       currentCustomerDetails?.customerId || updated[0]?.customerId,
        //     customerType: currentCustomerDetails?.customerType || "INDIVIDUAL",
        //     isDriver: "YES",
        //     identificationDocuments: capturedFiles.idDocs,
        //     drivingLicenseDocuments: capturedFiles.licenseDocs,
        //   };
        //   return updated;
        // });
        goToPage("AdditionalDriver");
      }
    } else {
      const { driverLicenseFront, driverLicenseBack, idFront, idBack } =
        data?.files || {};

      const hasAtLeastOneFile =
        driverLicenseFront || driverLicenseBack || idFront || idBack;
      if (data?.extractedData && hasAtLeastOneFile) {
        const updatedDetails = [...allDriverDetails];
        updatedDetails[currentDriver] = {
          ...updatedDetails[currentDriver],
          ...data.extractedData,
          identificationDocuments: [idFront, idBack].filter(Boolean),
          drivingLicenseDocuments: [
            driverLicenseFront,
            driverLicenseBack,
          ].filter(Boolean),
        };
        setAllDriverDetails(updatedDetails);
      } else {
        // If customer === driver1, refresh driver details with latest customer data
        const isCustomerSameAsDriver1 = Boolean(
          reservationDetails?.customerId &&
            reservationDetails?.driver1Id &&
            String(reservationDetails.customerId) ===
              String(reservationDetails.driver1Id)
        );

        if (
          isCustomerSameAsDriver1 &&
          currentDriver === 0 &&
          reservationDetails?.driver1Id
        ) {
          try {
            const refreshed = await BookingApi.getCustomerDetails(
              reservationDetails.driver1Id
            );
            setDriverDetailsAndInitialDriverIndexAtIndex(
              { ...refreshed.data },
              0
            );
          } catch (error) {
            console.error("Failed to refresh driver1 details:", error);
          }
        }
      }
      goToPage("DriverDetails");
    }
  };

  const handleBackUploadDocuments = () => {
    if (docStep > 1) {
      setDocStep(docStep - 1);
    } else {
      goBack();
      // if (currentCustomerDetails?.customerType === "COMPANY") {
      //   goBack();
      //   // setPage("PrivacyPolicyForm");
      // } else if (currentDriver === 1 || currentDriver === 2) {
      //   goBack();
      //   // setPage("AdditionalDriver");
      // } else {
      //   goBack();
      //   // setPage("RentalContractFirstDriver");
      // }
    }
  };
  const webCheckInBgImageUrl = appSettings?.config?.webCheckInBgImage;
  const hideBackgroundImageWebCheckIn =
    appSettings?.hideBackgroundImageWebCheckIn;
  const backgroundStyle = useMemo(() => {
    if (isSettingsLoading) {
      return { display: "none" };
    }
    const shouldShowImage = bgLoaded && !hideBackgroundImageWebCheckIn;
    return {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundImage: shouldShowImage
        ? `url(${
            webCheckInBgImageUrl?.trim()
              ? webCheckInBgImageUrl
              : "/assets/images/homePageImage.jpg"
          })`
        : "none",
      backgroundSize: "cover",
      backgroundPosition: "center",
      filter: "contrast(40%) brightness(90%)",
      transform: "scale(1.1)",
      zIndex: -1,
    };
  }, [
    bgLoaded,
    hideBackgroundImageWebCheckIn,
    webCheckInBgImageUrl,
    isSettingsLoading,
  ]);

  // Progress bar steps
  const stepGroups: string[][] = [
    ["BillingDetails"],
    ["PrivacyPolicyForm"],
    ["RentalContractFirstDriver"],
    ["UploadDocuments"],
    ["DriverDetails"],
    ["AdditionalDriver"],
    ["UploadDocuments"],
    ["DriverDetails"],
    ["AdditionalDriver"],
    ["UploadDocuments"],
    ["DriverDetails"],
    ["AdditionalDriver"],
    ["WebCheckCompletionPage"],
  ];

  const stepWeights: Record<string, number> = {
    BillingDetails: 4,
    PrivacyPolicyForm: 1,
    RentalContractFirstDriver: 1,
    UploadDocuments: 1,
    DriverDetails: 6,
    AdditionalDriver: 1,
    WebCheckCompletionPage: 1,
  };

  const totalWeight = stepGroups.reduce((sum, group) => {
    const key = group[0];
    return sum + (stepWeights[key] || 1);
  }, 0);

  const currentStepIndex = stepGroups.findIndex((group) =>
    group.includes(page)
  );

  let accumulatedWeight = 0;
  for (let i = 0; i < currentStepIndex; i++) {
    const key = stepGroups[i][0];
    accumulatedWeight += stepWeights[key] || 1;
  }

  const currentWeight = stepWeights[page] || 1;
  let progress = 0;

  if (currentStepIndex >= 0) {
    let adjustedAccumulated = accumulatedWeight;

    if (page === "DriverDetails") {
      let driverGlobalIndex = 0;
      for (let i = 0; i < currentStepIndex; i++) {
        if (stepGroups[i][0] === "DriverDetails") driverGlobalIndex++;
      }
      const sectionsPerDriver = 6;
      adjustedAccumulated +=
        driverGlobalIndex * currentWeight +
        ((currentDriver ?? 0) + 1) * (currentWeight / sectionsPerDriver);
    } else {
      adjustedAccumulated += currentWeight;
    }

    progress = (adjustedAccumulated / totalWeight) * 100;

    if (page === "WebCheckCompletionPage") {
      progress = 100;
    }
  }

  const handleNextPrivacyPolicy = (selectedIds: string[]) => {
    setSelectedPrivacyPolicy(selectedIds);
    if (currentCustomerDetails?.customerType === "COMPANY") {
      setShowDocumentInput("no");
      goToPage("UploadDocuments");
    } else {
      goToPage("RentalContractFirstDriver");
    }
  };

  if (showCompatibilityError) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          backgroundColor: "#f5f5f5",
        }}
      >
        <Box
          sx={{
            backgroundColor: "#fff",
            borderRadius: "12px",
            padding: "32px 24px",
            maxWidth: "480px",
            width: "100%",
            boxShadow: "0 2px 12px rgba(0,0,0,0.1)",
            textAlign: "center",
          }}
        >
          <Box sx={{ fontSize: "48px", marginBottom: "16px" }}>⚠️</Box>
          <Box
            sx={{
              fontSize: "16px",
              color: "#333",
              lineHeight: 1.6,
              fontWeight: 500,
            }}
          >
            {translate("device_compatibility_message")}
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          position: "relative",
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          overflow: "hidden",
          paddingTop: "5vh",
          paddingBottom: "5vh",
        }}
      >
        <Box sx={backgroundStyle} />

        {/* UploadVoucher Form */}
        {page === "UploadVoucher" &&
          (isSettingsLoading ? (
            <CardWithLoader isLoading={true} />
          ) : enableDogmaAI ? (
            <CardWithLoader isLoading={isLoading}>
              <UploadVoucher
                onNext={async (resDetails) => {
                  try {
                    setIsLoading(true);

                    setReservationDetails(resDetails);
                    getPrivacyPolicy(resDetails?.reservationId);
                    setDocumentTypes(resDetails?.documentTypes);

                    const customerDetails: AxiosResponse<CustomerDetailsResponse_2> =
                      await BookingApi.getCustomerDetails(
                        resDetails.customerId
                      );

                    const tempValues: DetailsFormValues = {
                      ...customerDetails.data,
                      isDriver: customerDetails.data.isDriver ? "YES" : "NO",
                      customerType:
                        customerDetails.data.customerType.toUpperCase(),
                    };

                    setInitialCustomerDetails({ ...tempValues });
                    setCurrentCustomerDetails({ ...tempValues });

                    goToPage("ReservationDetailsForm");
                  } catch (err) {
                    setModalMessage(
                      translate(
                        "Booking not found.\nPlease verify your details."
                      )
                    );
                    setOpenModal(true);
                  } finally {
                    setIsLoading(false);
                  }
                }}
                onManualEntry={() => goToPage("WebCheckInHome")}
                onShowModal={(message) => {
                  setModalMessage(message);
                  setOpenModal(true);
                }}
                progress={progress}
                onBack={() => goBack()}
                setIsLoading={setIsLoading}
                resDateIsPassed={resDateIsPassed}
                rezIsRental={rezIsRental}
                isWebCheckInDone={isWebCheckInDone}
                lowQualityUploadedImage={lowQualityUploadedImage}
              />
            </CardWithLoader>
          ) : (
            <NotAuthorisedPage />
          ))}

        {/* WebCheckInHome Form */}
        {page === "WebCheckInHome" && (
          <WebCheckInHome
            onSelect={(selectedPage) => goToPage(selectedPage)}
            isSmallScreen={isSmallScreen}
            showBackButton={!!enableDogmaAI}
            onBack={() => {
              resetToInitialState();
            }}
          />
        )}

        {/* SearchByReservationNumber Form */}
        <SuspenseWrapper>
          {page === "SearchByReservationNumber" && (
            <CardWithLoader isLoading={isLoading}>
              <SearchByReservationNumber
                onBack={() => goBack()}
                // onSearch={getBookingDetailsByReservationNumber}
                onSearch={async (data) => {
                  setLastSearchValues(data);
                  await getBookingDetailsByReservationNumber(data);
                }}
                progress={progress}
                defaultValues={lastSearchValues || DefaultValuesDetailsForm}
              />
            </CardWithLoader>
          )}
        </SuspenseWrapper>

        {/* SearchByVoucherNumber Form */}
        <SuspenseWrapper>
          {page === "SearchByVoucherNumber" && (
            <CardWithLoader isLoading={isLoading}>
              <SearchByVoucherNumber
                onBack={() => goBack()}
                onSearch={async (data) => {
                  setLastSearchValues(data);
                  await getBookingDetailsByVoucherCodeAndFullName(data);
                }}
                // onSearch={getBookingDetailsByVoucherCodeAndFullName}
                isSmallScreen={isSmallScreen}
                progress={progress}
                defaultValues={lastSearchValues || DefaultValuesDetailsForm}
              />
            </CardWithLoader>
          )}
        </SuspenseWrapper>

        {/* ReservationDetails Form*/}
        <SuspenseWrapper>
          {page === "ReservationDetailsForm" &&
            reservationDetails?.reservationId && (
              <ReservationDetails
                details={reservationDetails}
                onBack={() => {
                  goBack();
                }}
                onNext={() => {
                  goToPage("BillingDetails");
                }}
                progress={progress}
                currentCustomerDetails={currentCustomerDetails}
              />
            )}
        </SuspenseWrapper>

        {/* BillingDetails Form */}
        <SuspenseWrapper>
          {page === "BillingDetails" && (
            <BillingDetailsForm
              details={currentCustomerDetails}
              onBack={() => {
                goBack();
              }}
              onNext={(updatedDetails) => {
                setCurrentCustomerDetails(updatedDetails);
                if (
                  updatedDetails.customerId &&
                  updatedDetails.customerId !== reservationDetails.customerId
                ) {
                  setReservationDetails((prev) => ({
                    ...prev,
                    customerId: updatedDetails.customerId,
                  }));
                }
                goToPage("PrivacyPolicyForm");
              }}
              // onNext={() => {
              //   goToPage("PrivacyPolicyForm");
              // }}
              isSmallScreen={isSmallScreen}
              stepGroups={stepGroups}
              reservationDetails={reservationDetails}
            />
          )}
        </SuspenseWrapper>

        {/* PrivacyPolicy Form */}
        <SuspenseWrapper>
          {page === "PrivacyPolicyForm" && (
            <PrivacyPolicyForm
              details={allPrivacyPolicy}
              reservationDetails={reservationDetails}
              selectedPrivacyPolicy={selectedPrivacyPolicy}
              onBack={() => {
                goBack();
              }}
              onNext={(ids) => handleNextPrivacyPolicy(ids)}
              isSmallScreen={isSmallScreen}
              progress={progress}
            />
          )}
        </SuspenseWrapper>

        {/*RentalContractFirstDriver Form*/}
        <SuspenseWrapper>
          {page === "RentalContractFirstDriver" && (
            <RentalContractFirstDriver
              details={reservationDetails}
              onBack={() => {
                goBack();
              }}
              answer={showDocumentInput}
              onNext={(answer) => {
                setShowDocumentInput(answer);
                goToPage("UploadDocuments");
              }}
              progress={progress}
            />
          )}
        </SuspenseWrapper>

        {/*UploadDocuments Form*/}
        <SuspenseWrapper>
          {page === "UploadDocuments" && (
            <UploadDocuments
              section={docStep}
              onNext={handleNextFromDocs}
              details={reservationDetails}
              rentalAnswer={showDocumentInput}
              onBack={handleBackUploadDocuments}
              onShowModal={(message) => {
                setModalMessage(message);
                setOpenModal(true);
              }}
              documentTypes={documentTypes || []}
              isSmallScreen={isSmallScreen}
              stepGroups={stepGroups}
              currentDriver={currentDriver}
              currentCustomerDetails={currentCustomerDetails}
              lowQualityUploadedImage={lowQualityUploadedImage}
              savedFiles={allDriverDetails[currentDriver]}
            />
          )}
        </SuspenseWrapper>

        {/* DriverDetails Form */}
        <SuspenseWrapper>
          {page === "DriverDetails" && (
            <CardWithLoader isLoading={isLoading}>
              <DriverDetails
                reservationDetails={reservationDetails}
                driverNumber={currentDriver}
                documentTypes={documentTypes || []}
                currentCustomerDetails={currentCustomerDetails}
                onBack={() => {
                  goBack();
                }}
                onNext={handleDriverDetailsNext}
                allDriverDetails={allDriverDetails}
                setIsLoading={setIsLoading}
                isSmallScreen={isSmallScreen}
                stepGroups={stepGroups}
                rentalAnswer={showDocumentInput}
              />
            </CardWithLoader>
          )}
        </SuspenseWrapper>

        {/* AdditionalDriver Form */}
        <SuspenseWrapper>
          {page === "AdditionalDriver" && (
            <CardWithLoader isLoading={isLoading}>
              <AdditionalDriver
                details={allDriverDetails}
                onBack={() => goBack()}
                onNext={() => goToPage("WebCheckCompletionPage")}
                onAddDriver={(driverNumber) => {
                  setCurrentDriver(driverNumber);

                  setDocStep(1);
                  goToPage("UploadDocuments");
                }}
                allDriverDetails={allDriverDetails}
                allDriverCount={allDriverCount}
                isSmallScreen={isSmallScreen}
                progress={(driverIndex: number) =>
                  calculateNestedProgress({
                    currentPage: "AdditionalDriver",
                    currentSection: 1,
                    totalSections: 1,
                    stepGroups: stepGroups,
                    currentDriver: driverIndex,
                  })
                }
                currentDriver={currentDriver}
                reservationDetails={reservationDetails}
                setIsLoading={setIsLoading}
              />
            </CardWithLoader>
          )}
        </SuspenseWrapper>

        {/*WebCheckCompletionPage Form*/}
        <SuspenseWrapper>
          {page === "WebCheckCompletionPage" && (
            <WebCheckCompletionPage
              reservationDetails={reservationDetails}
              onBack={() => {
                goBack();
              }}
              onNext={() => {
                resetToInitialState();
              }}
              isSmallScreen={isSmallScreen}
              progress={progress}
            />
          )}
        </SuspenseWrapper>
      </Box>

      {/* ErrorPopup Form */}
      <WebCheckInPopUp
        openModal={openModal}
        modalMessage={modalMessage}
        onRequestClose={handleClose}
        isSmallScreen={isSmallScreen}
      />
    </>
  );
}
